import React, { useState, useEffect, useMemo } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import api from "../../URL/axiosConfig";

// UI Components
import {
  Grid,
  Modal,
  Backdrop,
  Fade,
  makeStyles,
  Button,
  ButtonGroup,
  CircularProgress,
} from "@material-ui/core";
import MUIDataTable from "mui-datatables";
import { Dropdown, Menu } from "antd";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

// Custom Components
import PageTitleMultipleButton from "../../components/PageTitle/PageTitleMultipleButton";
import ConfirmationModal from "../../components/Confimation/ConfirmationModal";
import Success from "../../components/message/Success";
import ImportCSV from "../../components/ModalComponent/ImportCSV";

// Modals
import AddNode from "./AddNode";
import AssignedNode from "./AssignedNode";
import ModalLogLastNode from "../../components/ModalComponent/ModalLogLastNode";
import ModalSetInterval from "../../components/ModalComponent/ModalSetInterval";
import ModalSetUtc from "../../components/ModalComponent/ModalSetUtc";
import ModalSetPulse from "../../components/ModalComponent/ModalSetPulse";
import ModalSetValve from "../../components/ModalComponent/ModalSetValve";
import ModalSetMeter from "../../components/ModalComponent/SetMeter";
import ModalSetTopup from "../../components/ModalComponent/ModalSetTopup";
import ModalSetSignal from "../../components/ModalComponent/ModalSignal";
import ModalDetailLocation from "../../components/ModalComponent/ModalDetailLocation";
import ModalJoin from "../../components/ModalComponent/ModalJoin";
import ModalDownlinkQueueNode from "../../components/ModalComponent/ModalDownlinkQueueNode";
import ModalDownlinkCustom from "../../components/ModalComponent/ModalDownlinkCustom";

const useStyles = makeStyles(theme => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper2: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "40%",
    outline: "none",
    borderRadius: 8,
  },
}));

// Konstanta untuk Tipe Modal agar tidak typo
const MODAL_TYPES = {
  ADD: "ADD",
  DELETE: "DELETE",
  UNSIGN: "UNSIGN",
  ASSIGN: "ASSIGN",
  IMPORT: "IMPORT",
  LOG: "LOG",
  INTERVAL: "INTERVAL",
  UTC: "UTC",
  PULSE: "PULSE",
  VALVE: "VALVE",
  METER: "METER",
  TOPUP: "TOPUP",
  SIGNAL: "SIGNAL",
  DETAIL_LOC: "DETAIL_LOC",
  JOIN: "JOIN",
  DOWNLINK_QUEUE: "DOWNLINK_QUEUE",
  DOWNLINK_BULK: "DOWNLINK_BULK",
};

// Konfigurasi Menu Dropdown (DRY Principle)
const MENU_OPTIONS = [
  { label: "Detail Node In Ns", type: MODAL_TYPES.LOG },
  { label: "Downlink Queue Node", type: MODAL_TYPES.DOWNLINK_QUEUE },
  { label: "Downlink Custom", type: MODAL_TYPES.DOWNLINK_BULK },

  // { label: "Set Interval", type: MODAL_TYPES.INTERVAL },
  // { label: "Set UTC", type: MODAL_TYPES.UTC },
  // { label: "Set Pulse", type: MODAL_TYPES.PULSE },
  // { label: "Set Valve", type: MODAL_TYPES.VALVE },
  // { label: "Set Meter", type: MODAL_TYPES.METER },
  // { label: "Set Topup", type: MODAL_TYPES.TOPUP },
  { label: "Show Signal", type: MODAL_TYPES.SIGNAL },
  // { label: "Detail Location", type: MODAL_TYPES.DETAIL_LOC }, // Uncomment jika dibutuhkan
  { label: "Join Request", type: MODAL_TYPES.JOIN },
];

function Node() {
  const classes = useStyles();

  // --- State Management ---
  const [data, setData] = useState([]);
  const [isBusy, setBusy] = useState(false);
  const [total, setTotal] = useState(0);

  // Table Params
  const [tableParams, setTableParams] = useState({
    page: 1,
    size: 10,
    search: null,
  });

  // Consolidated Modal State (Menggantikan banyak boolean)
  const [activeModal, setActiveModal] = useState(null); // 'ADD', 'DELETE', dll
  const [selectedNode, setSelectedNode] = useState(null); // Data node yang sedang dipilih

  // Feedback State
  const [feedback, setFeedback] = useState({
    open: false,
    text: "",
    type: "success",
  });

  // --- Handlers ---

  const handleOpenModal = (type, nodeData = null) => {
    setSelectedNode(nodeData);
    setActiveModal(type);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedNode(null);
  };

  const showFeedback = (text, type = "success") => {
    setFeedback({ open: true, text, type });
  };

  // --- API Functions ---

  const getData = async () => {
    setBusy(true);
    try {
      const res = await api({
        url: "/list/node",
        params: {
          page: tableParams.page,
          size: tableParams.size,
          search: tableParams.search,
        },
      });

      setTotal(res.data.total || 0);
      // Langsung set data mentah, biarkan Column Definition yang mengatur tampilan
      setData(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteNode = async () => {
    if (!selectedNode) return;
    try {
      await api({ url: "/delete/node/" + selectedNode.id, method: "POST" });
      showFeedback("Success Delete Node");
      handleCloseModal();
      getData();
    } catch (err) {
      showFeedback("Failed Delete Node", "error");
    }
  };

  const handleUnsignNode = async () => {
    if (!selectedNode) return;
    try {
      await api({
        url: "/ussigned/node/" + selectedNode.id + "/cms",
        method: "POST",
      });
      showFeedback("Success Unsign Node");
      handleCloseModal();
      getData();
    } catch (err) {
      showFeedback("Failed Unsign Node", "error");
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParams]);

  // --- Table Configuration ---

  const columns = useMemo(
    () => [
      { name: "application_name", label: "Application Name" },
      { name: "devEUI", label: "devEUI" },
      { name: "weiots_master_type.type_name", label: "Type Name" },
      { name: "weiots_middleware.name", label: "Middleware" },
      { name: "weiots_company.company_name", label: "Company Name" },
      {
        name: "installationDate",
        label: "Installation Date",
        options: {
          customBodyRender: val =>
            val ? moment(val).format("DD-MM-YYYY HH:mm:ss") : "-",
        },
      },
      { name: "child_app_name", label: "Slave Application Name" },
      { name: "child_sn", label: "Slave Serial Number" },
      {
        name: "Action",
        options: {
          filter: false,
          sort: false,
          empty: true,
          customBodyRender: (_, tableMeta) => {
            // Ambil object row data berdasarkan index
            const rowData = data[tableMeta.rowIndex];
            if (!rowData) return null;

            const isUnassigned = rowData.companyId == null;

            return (
              <ButtonGroup variant="contained">
                {isUnassigned ? (
                  <>
                    <Button
                      style={{ backgroundColor: "#E3184E", color: "#fff" }}
                      onClick={() =>
                        handleOpenModal(MODAL_TYPES.DELETE, rowData)
                      }
                    >
                      Delete
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() =>
                        handleOpenModal(MODAL_TYPES.ASSIGN, rowData)
                      }
                    >
                      Assigned
                    </Button>
                  </>
                ) : (
                  <Button
                    color="secondary"
                    onClick={() => handleOpenModal(MODAL_TYPES.UNSIGN, rowData)}
                  >
                    Unsign
                  </Button>
                )}

                <Dropdown
                  overlay={
                    <Menu>
                      {MENU_OPTIONS.map(item => (
                        <Menu.Item
                          key={item.type}
                          onClick={() => handleOpenModal(item.type, rowData)}
                        >
                          {item.label}
                        </Menu.Item>
                      ))}
                    </Menu>
                  }
                  trigger={["click"]}
                >
                  <Button color="primary" size="small">
                    <ArrowDropDownIcon />
                  </Button>
                </Dropdown>
              </ButtonGroup>
            );
          },
        },
      },
    ],
    [data],
  );

  // --- Modal Rendering Helper ---
  const renderModals = () => {
    // Helper untuk properti umum modal
    const commonProps = {
      open: true,
      setOpen: handleCloseModal, // Beberapa modal pakai setOpen(false)
      onclose: handleCloseModal, // Beberapa modal pakai onclose
      devEui: selectedNode?.devEUI,
      node_id: selectedNode?.id,
      getData: getData,
    };

    switch (activeModal) {
      case MODAL_TYPES.ADD:
        return (
          <Modal
            className={classes.modal}
            open={true}
            onClose={handleCloseModal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
          >
            <Fade in={true}>
              <div className={classes.paper2}>
                <AddNode onclose={handleCloseModal} />
              </div>
            </Fade>
          </Modal>
        );
      case MODAL_TYPES.ASSIGN:
        return (
          <Modal
            className={classes.modal}
            open={true}
            onClose={handleCloseModal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
          >
            <Fade in={true}>
              <div className={classes.paper2}>
                <AssignedNode
                  getdata={getData}
                  onclose={handleCloseModal}
                  idnode={selectedNode?.id}
                />
              </div>
            </Fade>
          </Modal>
        );
      case MODAL_TYPES.DELETE:
        return (
          <ConfirmationModal
            open={true}
            setOpen={handleCloseModal}
            title="Are you sure to delete this Node?"
            desc="Node deleted can't be restored"
            onCancel={handleCloseModal}
            onConfirm={handleDeleteNode}
          />
        );
      case MODAL_TYPES.UNSIGN:
        return (
          <ConfirmationModal
            open={true}
            setOpen={handleCloseModal}
            title="Are you sure to Unsign this Node?"
            desc="Node unsign can't be restored"
            onCancel={handleCloseModal}
            onConfirm={handleUnsignNode}
          />
        );
      case MODAL_TYPES.IMPORT:
        return (
          <ImportCSV
            modal={true}
            setModal={handleCloseModal}
            getData={getData}
          />
        );

      // Dynamic Data Modals
      case MODAL_TYPES.DOWNLINK_QUEUE:
        return <ModalDownlinkQueueNode {...commonProps} />;
      case MODAL_TYPES.DOWNLINK_BULK:
        return <ModalDownlinkCustom {...commonProps} />;
      case MODAL_TYPES.LOG:
        return <ModalLogLastNode {...commonProps} />;
      case MODAL_TYPES.INTERVAL:
        return <ModalSetInterval {...commonProps} />;
      case MODAL_TYPES.UTC:
        return <ModalSetUtc {...commonProps} />;
      case MODAL_TYPES.PULSE:
        return <ModalSetPulse {...commonProps} />;
      case MODAL_TYPES.VALVE:
        return <ModalSetValve {...commonProps} />;
      case MODAL_TYPES.METER:
        return <ModalSetMeter {...commonProps} />;
      case MODAL_TYPES.TOPUP:
        return <ModalSetTopup {...commonProps} />;
      case MODAL_TYPES.SIGNAL:
        return <ModalSetSignal {...commonProps} />;
      case MODAL_TYPES.DETAIL_LOC:
        return <ModalDetailLocation {...commonProps} />;
      case MODAL_TYPES.JOIN:
        return <ModalJoin {...commonProps} devEUI={selectedNode?.devEUI} />;

      default:
        return null;
    }
  };

  return (
    <>
      {/* Feedback Message */}
      <Success
        text={feedback.text}
        open={feedback.open}
        setOpen={val => setFeedback({ ...feedback, open: val })}
        type={feedback.type}
      />

      {/* Render Active Modal */}
      {renderModals()}

      <PageTitleMultipleButton
        button={[
          {
            buttonName: "Add Node",
            onClick: () => handleOpenModal(MODAL_TYPES.ADD),
          },
          {
            buttonName: "Import CSV",
            onClick: () => handleOpenModal(MODAL_TYPES.IMPORT),
          },
        ]}
        title="Node"
      />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <MUIDataTable
            data={data}
            columns={columns}
            options={{
              filter: false,
              selectableRows: "multiple",
              filterType: "dropdown",
              responsive: "stacked",
              serverSide: true, // Penting untuk pagination server
              count: total,
              rowsPerPage: tableParams.size,
              page: tableParams.page - 1, // MUIDataTable pakai 0-index
              textLabels: {
                body: {
                  noMatch: isBusy ? (
                    <CircularProgress disableShrink />
                  ) : (
                    "Sorry, there is no matching data to display"
                  ),
                },
              },
              onTableChange: (action, tableState) => {
                switch (action) {
                  case "changePage":
                    setTableParams(prev => ({
                      ...prev,
                      page: tableState.page + 1,
                    }));
                    break;
                  case "changeRowsPerPage":
                    setTableParams(prev => ({
                      ...prev,
                      size: tableState.rowsPerPage,
                      page: 1,
                    }));
                    break;
                  default:
                    break;
                }
              },
              onSearchChange: searchText => {
                setTableParams(prev => ({
                  ...prev,
                  search: searchText,
                  page: 1,
                }));
              },
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default withRouter(Node);
